import * as React from 'react';
import type { SerializedStyles, Theme } from '@emotion/react';
import { css } from '@emotion/react';
import { Box, useBreakpoint } from '@resi-media/resi-ui';
import { Outlet, useLocation } from 'react-router';
import {
  HEADER_HEIGHT,
  LAYOUT_CONTAINER_ID,
  MOBILE_HEADER_HEIGHT,
  MOBILE_SEARCH_HEADER_HEIGHT,
} from '@sites/constants/layout';
import { useFeed } from '@sites/contexts/feedContext';
import { useQueryParams } from '@sites/contexts/queryContext';
import { useSearch } from '@sites/contexts/searchContext';
import { useHeaderVisibility } from '@sites/hooks/useHeaderVisibility';
import * as globalStyles from '@sites/styles/global';
import Loading from '@studio/pages/Loading';
import { Footer } from '../Footer';
import { Header } from '../Header';

const S = {
  appWrapper: (
    theme: Theme,
    {
      headerVisibility,
      isEmbed,
      isMobileAndSearchOpen,
    }: { headerVisibility: boolean; isEmbed: boolean; isMobileAndSearchOpen: boolean }
  ): SerializedStyles => css`
    position: relative;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-top: 0;
    ${!isEmbed &&
    headerVisibility &&
    `  
      padding-top: ${isMobileAndSearchOpen ? MOBILE_SEARCH_HEADER_HEIGHT : MOBILE_HEADER_HEIGHT}px;
      ${theme.mq.xl} {
        padding-top: ${HEADER_HEIGHT}px;
      }
  `}
    transition: padding 0.3s ease;
  `,
  content: (theme: Theme, { isEmbed }: { isEmbed: boolean }): SerializedStyles => css`
    width: 100%;
    height: 100%;
    overscroll-behavior: none;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    background-color: ${isEmbed ? theme.palette.contrastInverse : theme.palette.surface.default};
    display: flex;
    flex-flow: column nowrap;
    /* Prevent scrollbar jumps by reserving space for scrollbar */
    scrollbar-gutter: stable;
  `,
};

const Layout = () => {
  const { site } = useFeed();
  const { isEmbed } = useQueryParams();
  const { isSearchInputExpanded } = useSearch();
  const headerVisibility = useHeaderVisibility();
  const mediaQuery = useBreakpoint();
  const isMobileAndSearchOpen = !mediaQuery.lg && isSearchInputExpanded;
  const { pathname } = useLocation();

  React.useEffect(() => {
    /**
     * Scroll to top on route changes
     * does not handle pagination or video page due to padding
     */
    const container = document.getElementById(LAYOUT_CONTAINER_ID);
    if (container) {
      container.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [pathname]);

  return (
    <Box
      css={(theme) =>
        S.appWrapper(theme, {
          headerVisibility,
          isEmbed,
          isMobileAndSearchOpen,
        })
      }
      data-testid="layout__container"
    >
      {!isEmbed && (
        <Box css={(theme) => globalStyles.searchInputWrapper(theme, headerVisibility)}>
          <Header isMobileAndSearchOpen={isMobileAndSearchOpen} siteName={site?.name} />
        </Box>
      )}
      <React.Suspense fallback={<Loading />}>
        <Box css={(theme) => S.content(theme, { isEmbed })} dataTestId="layout__content" id={LAYOUT_CONTAINER_ID}>
          <Box
            className="layout__outlet"
            css={css({
              flexBasis: '100%',
            })}
          >
            <Outlet />
          </Box>
          {!isEmbed && <Footer />}
        </Box>
      </React.Suspense>
    </Box>
  );
};

Layout.displayName = 'Layout';

export { Layout };
